// Composables
import { createRouter, createWebHistory } from 'vue-router'
import { ensureAuth } from '@/plugins/firebase'
import * as Sentry from '@sentry/vue'
import { useProjectStore } from '@/store/projectStore.js'
import { useDynamicFormStore } from '@/store/dynamicFormStore.js'
import { useProductStore } from '@/store/productStore.js'
import { useVendorStore } from '@/store/vendorStore.js'
import { useProjectUsersStore } from '@/store/projectUsersStore'
import { useLocaleStore } from '@/store/localeStore.js'
import pinia from '@/store'
// Eager on purpose: they must render even when the network cannot fetch a lazy chunk (#41).
import MainLayout from '@/layouts/main/MainLayout.vue'
import StatusView from '@/views/StatusView.vue'

const dynamicFormStore = useDynamicFormStore(pinia)
const projectStore = useProjectStore(pinia)
const projectUsersStore = useProjectUsersStore(pinia)

const routesWithoutSteps = ['inscription-landing', 'inscription-upload-waiver']

const stepsAllowingPriceOptionDeepLink = ['inscription-step-1', 'inscription-step-2']

// Child → child navigation (browser back/forward after a reload bounced the user
// to landing) does not re-run the parent beforeEnter, so the steps after the
// forms must check the store themselves (#55, Sentry WEB-15).
const hasRequiredFormData = () => {
  const player = projectStore.form?.players?.find((p) => !p.isExtra)
  if (!player?.playerForm) return false
  if (dynamicFormStore.dataUser?.parentProfileFields && !projectStore.form?.parentForm) return false
  return true
}

const requireFormData = (to, from, next) => {
  if (hasRequiredFormData()) return next()
  const { organizationId, projectId } = to.params
  next({ name: 'inscription-landing', params: { organizationId, projectId } })
}

const bindWaiverData = async ({ organizationId, projectId }) => {
  if (!projectStore.waiver.length) await projectStore.bindWaiver({ organizationId, projectId })
  if (!projectStore.staticContentWaiver.length) await projectStore.bindStaticContentWaiver()
}

export const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      // The root has no project to show; without this the header renders a null project (#37).
      { path: '', redirect: { name: '404' } },
      {
        path: '/inscription/organization/:organizationId/project/:projectId',
        name: 'inscription',
        component: () => import('@/views/inscription/InscriptionView.vue'),
        props: true,
        children: [
          {
            path: 'landing',
            name: 'inscription-landing',
            component: () => import('@/views/LandingView.vue'),
            props: true,
            meta: {
              stepId: () => null,
            },
          },
          {
            path: 'upload-waiver',
            name: 'inscription-upload-waiver',
            component: () => import('@/views/inscription/components/form/steps/4Waiver.vue'),
            meta: {
              stepId: () => null,
            },
            props: true,
            beforeEnter: async (to, from, next) => {
              const { organizationId, projectId } = to.params
              await bindWaiverData({ organizationId, projectId })
              next()
            },
          },
          {
            path: 'step-1',
            name: 'inscription-step-1',
            component: () => import('@/views/inscription/components/form/steps/1ParentForm.vue'),
            meta: {
              stepId: () => projectStore.stepId1,
            },
            props: true,
            beforeEnter: async (to, from, next) => {
              const { organizationId, projectId } = to.params
              if (!projectUsersStore.parents.length)
                await projectUsersStore.bindParents({
                  organizationId,
                  projectId,
                })
              next()
            },
          },
          {
            path: 'step-2',
            name: 'inscription-step-2',
            component: () => import('@/views/inscription/components/form/steps/2PlayerForm.vue'),
            meta: {
              stepId: () => projectStore.stepId2,
            },
            props: true,
          },
          {
            path: 'step-3',
            name: 'inscription-step-3',
            component: () => import('@/views/products/ProductsListView.vue'),
            meta: {
              stepId: () => projectStore.stepId3,
            },
            props: true,
            beforeEnter: requireFormData,
          },
          {
            path: 'step-4',
            name: 'inscription-step-4',
            component: () => import('@/views/inscription/components/form/steps/4Waiver.vue'),
            meta: {
              stepId: () => projectStore.stepId4,
            },
            props: true,
            beforeEnter: [
              requireFormData,
              async (to, from, next) => {
                const { organizationId, projectId } = to.params
                await bindWaiverData({ organizationId, projectId })
                next()
              },
            ],
          },
          {
            path: 'step-5',
            name: 'inscription-step-5',
            component: () => import('@/views/inscription/components/form/steps/5ChoosePaymentType.vue'),
            meta: {
              stepId: () => projectStore.stepId5,
            },
            props: true,
            beforeEnter: requireFormData,
          },
          {
            path: 'step-6',
            name: 'inscription-step-6',
            component: () => import('@/views/inscription/components/form/steps/6PaymentForm.vue'),
            meta: {
              stepId: () => projectStore.stepId6,
            },
            props: true,
            beforeEnter: requireFormData,
          },
        ],
        beforeEnter: async (to, from, next) => {
          const { organizationId, projectId } = to.params
          const { email } = to.query
          try {
            await ensureAuth()

            const productStore = useProductStore()
            const vendorStore = useVendorStore()
            const localeStore = useLocaleStore()

            await projectStore.bindProject({ organizationId, projectId })
            // Stop before anything reads project fields (Sentry WEB-B: setLocale on a null project).
            if (!projectStore.project) return next({ name: '404' })
            if (projectStore.project?.pricing?.priceOptions?.[0]?.id) {
              const priceOption = projectStore.project.pricing.priceOptions[0]
              projectStore.setPriceOptionId(priceOption.id)
              projectStore.setPrice(priceOption)
            }
            const queryPriceOptionId = to.query.priceOption
            let hasValidPriceOptionFromQuery = false
            if (queryPriceOptionId && stepsAllowingPriceOptionDeepLink.includes(to.name)) {
              const priceOption = projectStore.project?.pricing?.priceOptions
                ?.find(p => p.id === queryPriceOptionId)
              if (priceOption) {
                projectStore.setPriceOptionId(priceOption.id)
                projectStore.setPrice(priceOption)
                projectStore.updatePaymentInfoTranslations()
                hasValidPriceOptionFromQuery = true
              }
            }
            await dynamicFormStore.bindDataUser({ organizationId, projectId })
            await productStore.bindProducts({ organizationId, projectId })
            await vendorStore.bindVendor({ organizationId, projectId })
            await projectStore.bindTerms({ organizationId, projectId })
            await projectUsersStore.bindPlayers({ organizationId, projectId })
            await localeStore.setLocale()

            const allowedEmails = projectStore?.project?.allowedEmails || []
            const deadline = projectStore.project?.deadline?.toDate()

            if (!projectStore?.project) {
              next({ name: '404' })
            } else if ((deadline && deadline < new Date() && !allowedEmails.includes(email)) && to.name !== 'inscription-upload-waiver') {
              next({ name: 'DeadLine' })
            } else if ((!projectStore.project?.active || !projectStore.project?.published) && to.name !== 'inscription-upload-waiver') {
              next({ name: 'Closed' })
            } else if ((projectStore.project.stock !== null && projectStore.project.stock <= 0) && to.name !== 'inscription-upload-waiver') {
              next({ name: 'SoldOut' })
            } else if ( !routesWithoutSteps.includes(to.name) && !hasValidPriceOptionFromQuery ) {
              next({
                name: 'inscription-landing',
                params: { organizationId, projectId },
              })
            } else {
              // Permitir navegación a otros steps
              next()
            }
          } catch (error) {
            // A failed session or data load used to abort the guard and leave a blank screen (#39).
            Sentry.captureException(error, { tags: { organizationId, projectId, route: to.name } })
            next({ name: 'ConnectionError', query: { redirect: to.fullPath } })
          }
        },
      },
      {
        path: '/404',
        name: '404',
        component: StatusView,
        props: {
          type: 'notFound',
          title: '404.sorry',
          description: '404.message',
        },
      },
      {
        path: '/sold-out',
        name: 'SoldOut',
        component: StatusView,
        props: {
          type: 'soldOut',
          title: 'soldOut.title',
          description: 'soldOut.subTitle',
        },
      },
      {
        path: '/dead-line',
        name: 'DeadLine',
        component: StatusView,
        props: {
          type: 'deadLine',
          title: 'deadLine.title',
          description: 'deadLine.description',
        },
      },
      {
        path: '/closed',
        name: 'Closed',
        component: StatusView,
        props: {
          type: 'closed',
          title: 'closed.title',
          description: 'closed.description',
        },
      },
      {
        path: '/connection-error',
        name: 'ConnectionError',
        component: StatusView,
        props: {
          type: 'connectionError',
          title: 'connectionError.title',
          description: 'connectionError.description',
        },
      },
      { path: '/:pathMatch(.*)*', redirect: '404' },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
})

export default router
